<template>
  <IonButton
    :type="nativeType"
    :disabled="isDisabled || isLoading"
    :size="size"
    :fill="ionFill"
    :color="color"
    :expand="isExpanded ? 'block' : undefined"
    :shape="isRound ? 'round' : undefined"
    :aria-busy="isLoading || undefined"
    :aria-label="ariaLabel"
    class="custom-ion-button"
    :class="[
      `is-fill-${fill}`,
      `size-${size}`,
      color,
      { 'shape-round': isRound, 'is-loading': isLoading },
    ]"
    @click="onClick"
  >
    <IonSpinner v-if="isLoading" name="dots" class="custom-ion-button-spinner" />
    <span
      v-show="!isLoading"
      class="row-center custom-ion-button-content justify-center"
      :class="FONT_WEIGHT_MAP[fontWeight]"
    >
      <slot />
    </span>
  </IonButton>
</template>

<script lang="ts">
import {
  ComponentColorType,
  CustomButtonFontWeight,
  CustomButtonNativeType,
  ComponentSizeType,
  CustomIonButtonType,
} from "@/types/componentType";
import { FONT_WEIGHT_MAP } from "@/constants/sizing";
import { IonButton, IonSpinner } from "@ionic/vue";
import { PropType, computed, defineComponent } from "vue";

export default defineComponent({
  name: "CustomIonButton",
  components: { IonButton, IonSpinner },
  props: {
    fill: { type: String as PropType<CustomIonButtonType>, default: "solid" },
    color: { type: String as PropType<ComponentColorType>, default: "primary" },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
    nativeType: { type: String as PropType<CustomButtonNativeType>, default: "button" },
    fontWeight: { type: String as PropType<CustomButtonFontWeight>, default: "normal" },
    isDisabled: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    isExpanded: { type: Boolean, default: false },
    isRound: { type: Boolean, default: false },
    ariaLabel: { type: String, default: undefined },
  },
  emits: ["onClick"],
  setup(props, { emit }) {
    const ionFill = computed(() => (props.fill === "surface" ? "clear" : props.fill));
    function onClick(event: MouseEvent) {
      if (props.isDisabled || props.isLoading) {
        return;
      }
      emit("onClick", event);
    }
    return { ionFill, onClick, FONT_WEIGHT_MAP };
  },
});
</script>

<style lang="scss">
.custom-ion-button {
  --padding-top: 0;
  --padding-bottom: 0;
  --padding-start: var(--control-pad-x);
  --padding-end: var(--control-pad-x);
  --border-radius: var(--button-radius);
  &.size-small,
  &.size-default,
  &.size-large {
    min-height: var(--control-height);
  }
  &.shape-round {
    --border-radius: var(--pill-radius);
  }
  &::part(native) {
    min-height: var(--control-height);
    font-size: var(--control-font);
  }
  &.is-fill-surface {
    &::part(native) {
      background-color: var(--color-light-translucent);
    }
    &:not(.button-disabled):hover {
      opacity: 1;
      &::part(native) {
        background-color: var(--color-light-light);
      }
    }
    &.ion-activated::part(native) {
      background: var(--color-light-bg);
    }
  }
  &.button-disabled:not(.is-loading) {
    opacity: 1;
    &::part(native) {
      color: var(--color-medium-dark);
    }
    &.is-fill-solid::part(native) {
      background: var(--color-medium-bg);
    }
    &.is-fill-outline::part(native) {
      background: transparent;
      border-color: var(--color-medium);
    }
    &.is-fill-clear::part(native) {
      background: transparent;
    }
    &.is-fill-surface::part(native) {
      background: var(--color-light);
    }
  }
}
</style>
